import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    ButtonComponent,
    ConfirmationDialogComponent,
    MenuModule,
    MenuTriggerDirective,
    TextFieldComponent,
    TooltipDirective,
} from '@pure/hive';
import { IConversation, IConversationGroup } from '../model/conversation';
import { FromNowImpurePipe } from '../pipes/from-now-impure.pipe';
import moment from 'moment';
import { lowerCase, orderBy, trim } from 'lodash-es';
import { faMagnifyingGlass, faPlus } from '@fortawesome/pro-light-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';

@Component({
    selector: 'copilot-conversation-panel',
    standalone: true,
    imports: [
        FormsModule,
        ButtonComponent,
        TextFieldComponent,
        TooltipDirective,
        MenuTriggerDirective,
        MenuModule,
        ConfirmationDialogComponent,
        FromNowImpurePipe,
    ],
    templateUrl: './conversation-panel.component.html',
    styleUrl: './conversation-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationPanelComponent implements OnChanges {
    @Input() conversations: IConversation[] = [];
    @Input() activeConversation: IConversation | null = null;
    @Input() showTitle: boolean = true;
    @Output() selectConversation = new EventEmitter<IConversation>();
    @Output() deleteConversation = new EventEmitter<IConversation>();
    @Output() exportConversation = new EventEmitter<IConversation>();
    @Output() newConversation = new EventEmitter<void>();
    @ViewChild('deleteConfirmationDialog')
    deleteConfirmationDialog!: ConfirmationDialogComponent;

    conversationToDelete: IConversation | null = null;
    conversationGroups: IConversationGroup[] = [];
    filter: string = '';

    constructor(private readonly cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['conversations']) {
            this.updateConversationGroups();
        }
    }

    clickConversation(convo: IConversation): void {
        this.selectConversation.emit(convo);
    }

    clickDelete(convo: IConversation): void {
        this.conversationToDelete = convo;
        this.deleteConfirmationDialog.show();
    }

    clickDownload(convo: IConversation): void {
        this.exportConversation.emit(convo);
    }

    clickConfirmDelete(): void {
        this.deleteConfirmationDialog.hide();
        this.deleteConversation.emit(this.conversationToDelete);
        this.conversationToDelete = null;
    }

    clickCancelDelete(): void {
        this.deleteConfirmationDialog.hide();
        this.conversationToDelete = null;
    }

    isActiveConversation(convo: IConversation): boolean {
        return this.activeConversation != null && this.activeConversation.sessionId === convo.sessionId;
    }

    onFilterChange(newValue: string): void {
        this.filter = newValue;
        this.updateConversationGroups();
    }

    private updateConversationGroups(): void {
        // Apply filter to conversations
        const filterText = lowerCase(trim(this.filter));
        const filteredConversations = this.conversations.filter(convo =>
            lowerCase(trim(convo.title)).includes(filterText),
        );

        // Create empty groups
        this.conversationGroups = [
            {
                oldestTime: moment().startOf('day').valueOf(),
                title: 'Today',
                conversations: [],
            },
            {
                oldestTime: moment().startOf('day').subtract(1, 'day').valueOf(),
                title: 'Yesterday',
                conversations: [],
            },
            {
                oldestTime: moment()
                    .startOf('day')

                    .subtract(7, 'days')
                    .valueOf(),
                title: 'Past week',
                conversations: [],
            },
            {
                oldestTime: null,
                title: 'Older',
                conversations: [],
            },
        ];

        // Populate groups. Each conversation goes into the first group it fits in.
        orderBy(filteredConversations, convo => convo.lastUpdated.valueOf(), 'desc').forEach(convo => {
            this.conversationGroups
                .find(group => group.oldestTime == null || convo.lastUpdated.valueOf() >= group.oldestTime.valueOf())
                .conversations?.push(convo);
        });

        // Remove empty groups
        this.conversationGroups = this.conversationGroups.filter(group => group.conversations.length > 0);

        //trigger change detection
        this.cdr.markForCheck();
    }

    protected readonly faEllipsis = faEllipsis;
    protected readonly faMagnifyingGlass = faMagnifyingGlass;
    protected readonly faPlus = faPlus;
}
